import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, AlertCircle } from 'lucide-react';
import { useActualites } from '../hooks/usePublicApi';
import { useTexts } from '../hooks/useTexts';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';

const formatDate = (value: string) => {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const resolveImageUrl = (url?: string | null) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const base = import.meta.env.VITE_API_BASE_URL || '';
  const normalizedBase = base.endsWith('/api') ? base.replace(/\/api$/, '') : base;
  return `${normalizedBase}${url}`;
};

export default function ActualitesPage() {
  const { getText } = useTexts();
  const [page, setPage] = useState(1);

  const { data: news, loading, error, meta } = useActualites({ page, ordering: '-date_publication' });

  const pageSize = news.length || 1;
  const totalPages = useMemo(() => {
    if (!meta || typeof meta.count !== 'number' || meta.count === 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(meta.count / pageSize));
  }, [meta, pageSize]);

  const canGoPrevious = page > 1 && !!meta?.previous;
  const canGoNext = !!meta?.next;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {(() => {
              const title = getText('news.title');
              return title === 'news.title' ? 'Actualités' : title;
            })()}
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {(() => {
              const subtitle = getText('news.subtitle');
              return subtitle === 'news.subtitle'
                ? 'Découvrez nos dernières actualités, événements et informations importantes.'
                : subtitle;
            })()}
          </p>
        </header>

        {loading && (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        )}

        {error && !loading && (
          <ErrorMessage message={error} />
        )}

        {!loading && !error && news.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center border border-dashed border-gray-200">
            <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {(() => {
                const text = getText('news.empty');
                return text === 'news.empty' ? 'Aucune actualité pour le moment' : text;
              })()}
            </h2>
            <p className="text-gray-500">
              {(() => {
                const text = getText('news.empty_description');
                return text === 'news.empty_description'
                  ? 'Revenez bientôt pour découvrir nos prochaines informations.'
                  : text;
              })()}
            </p>
          </div>
        )}

        <div className="grid gap-8 md:grid-cols-2">
          {news.map((item) => {
            const imageUrl = resolveImageUrl(item.image_url_final);
            const excerpt = item.resume?.trim() || item.contenu.slice(0, 220);
            return (
              <article
                key={item.id}
                className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100 hover:border-blue-200 transition"
              >
                {imageUrl && (
                  <Link to={`/news/${item.slug}`}>
                    <img
                      src={imageUrl}
                      alt={item.titre}
                      className="w-full h-56 object-cover"
                      loading="lazy"
                    />
                  </Link>
                )}
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-3 space-x-3">
                    <span className="inline-flex items-center">
                      <Calendar className="w-4 h-4 mr-1.5" />
                      {formatDate(item.date_publication)}
                    </span>
                    {item.projet_nom && (
                      <span className="inline-flex items-center text-blue-600 font-medium">
                        {item.projet_nom}
                      </span>
                    )}
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                    <Link to={`/news/${item.slug}`} className="hover:text-blue-600 transition-colors">
                      {item.titre}
                    </Link>
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {excerpt}
                    {item.contenu.length > excerpt.length && '...'}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-400">
                      {item.auteur_nom}
                    </div>
                    <Link
                      to={`/news/${item.slug}`}
                      className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                    >
                      {(() => {
                        const text = getText('news.read_more');
                        return text === 'news.read_more' ? 'Lire la suite' : text;
                      })()}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {(canGoPrevious || canGoNext) && (
          <div className="flex items-center justify-center mt-12 space-x-4">
            <button
              type="button"
              disabled={!canGoPrevious}
              onClick={() => canGoPrevious && setPage((prev) => Math.max(1, prev - 1))}
              className={`px-4 py-2 rounded-lg border ${
                canGoPrevious
                  ? 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  : 'border-gray-200 text-gray-400 cursor-not-allowed'
              } transition`}
            >
              {(() => {
                const text = getText('pagination.previous');
                return text === 'pagination.previous' ? 'Précédent' : text;
              })()}
            </button>
            <span className="text-sm font-medium text-gray-600">
              {(() => {
                const text = getText('pagination.page');
                const label = text === 'pagination.page' ? 'Page {current} sur {total}' : text;
                return label
                  .replace('{current}', String(page))
                  .replace('{total}', String(totalPages));
              })()}
            </span>
            <button
              type="button"
              disabled={!canGoNext}
              onClick={() => canGoNext && setPage((prev) => prev + 1)}
              className={`px-4 py-2 rounded-lg border ${
                canGoNext
                  ? 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  : 'border-gray-200 text-gray-400 cursor-not-allowed'
              } transition`}
            >
              {(() => {
                const text = getText('pagination.next');
                return text === 'pagination.next' ? 'Suivant' : text;
              })()}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

